import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  SupplyProfileCreateDeep,
  SupplyProfileResponse,
  SupplyProfileUpdateDeep,
} from "client/jspPlatformExperiment";
import jspPlatformApi from "client/portals";
import {
  isApiError,
  isErrorResponseBodyType,
  toJspApiError,
} from "models/utils/apiUtils";

interface SupplyProfilesState {
  /** Every profile the API returned, across all sites */
  profiles: SupplyProfileResponse[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null | undefined;
}

const initialState: SupplyProfilesState = {
  profiles: [],
  isLoading: false,
  isSaving: false,
  error: null,
};

const getErrorMessage = (error: unknown): string => {
  const err = isApiError(error) ? toJspApiError(error) : null;
  if (err && isErrorResponseBodyType(err.body)) {
    return err.body.detail;
  }
  return "Unknown error!";
};

const isForbidden = (error: unknown): boolean =>
  isApiError(error) && toJspApiError(error).status === 403;

/**
 * Load every profile, inactive ones included. Non-admins get a 403 on the
 * admin listing, in which case the active-only listing is used instead.
 */
const fetchSupplyProfiles = createAsyncThunk(
  "supplyProfiles/fetchSupplyProfiles",
  async (_input: undefined, { rejectWithValue }) => {
    try {
      const res =
        await jspPlatformApi().gfsOrderingSupplyProfile.getInventoryGfsOrderingFacilityAdminAll(
          // A 403 here is expected for non-admins; the fallback below
          // handles it, so no global error snackbar.
          { suppressErrorPrompt: true },
        );
      return res.data ?? [];
    } catch (error) {
      if (!isForbidden(error)) {
        return rejectWithValue(getErrorMessage(error));
      }
    }
    return jspPlatformApi()
      .gfsOrderingSupplyProfile.getInventoryGfsOrderingFacility()
      .then((res) => res.data ?? [])
      .catch((error) => rejectWithValue(getErrorMessage(error)));
  },
);

const createSupplyProfile = createAsyncThunk(
  "supplyProfiles/createSupplyProfile",
  async (input: { body: SupplyProfileCreateDeep }, { rejectWithValue }) =>
    jspPlatformApi()
      .gfsOrderingSupplyProfile.postInventoryGfsOrderingFacility({
        body: input.body,
      })
      .then((res) => res.data ?? null)
      .catch((error) => rejectWithValue(getErrorMessage(error))),
);

const updateSupplyProfile = createAsyncThunk(
  "supplyProfiles/updateSupplyProfile",
  async (
    input: { profileId: number; body: SupplyProfileUpdateDeep },
    { rejectWithValue },
  ) =>
    jspPlatformApi()
      .gfsOrderingSupplyProfile.putInventoryGfsOrderingFacilityByProfileId({
        path: { profile_id: input.profileId },
        body: input.body,
      })
      .then((res) => res.data ?? null)
      .catch((error) => rejectWithValue(getErrorMessage(error))),
);

const deactivateSupplyProfile = createAsyncThunk(
  "supplyProfiles/deactivateSupplyProfile",
  async (input: { profileId: number }, { rejectWithValue }) =>
    jspPlatformApi()
      .gfsOrderingSupplyProfile.patchInventoryGfsOrderingFacilityByProfileIdDeactivate(
        { path: { profile_id: input.profileId } },
      )
      .then((res) => res.data ?? null)
      .catch((error) => rejectWithValue(getErrorMessage(error))),
);

const reactivateSupplyProfile = createAsyncThunk(
  "supplyProfiles/reactivateSupplyProfile",
  async (input: { profileId: number }, { rejectWithValue }) =>
    jspPlatformApi()
      .gfsOrderingSupplyProfile.patchInventoryGfsOrderingFacilityByProfileIdReactivate(
        { path: { profile_id: input.profileId } },
      )
      .then((res) => res.data ?? null)
      .catch((error) => rejectWithValue(getErrorMessage(error))),
);

/**
 * One click on the ingredient board: point a food at one active profile of
 * its site (or at none). The server moves it off every other active
 * profile of the site in the same request.
 */
const setIngredientLink = createAsyncThunk(
  "supplyProfiles/setIngredientLink",
  async (
    input: {
      siteId: number;
      foodId: number;
      foodName: string;
      profileId: number | null;
    },
    { rejectWithValue },
  ) =>
    jspPlatformApi()
      .gfsOrderingSupplyProfile.putInventoryGfsOrderingFacilityIngredientLink({
        body: {
          jasper_site_id: input.siteId,
          food_id: input.foodId,
          supply_profile_id: input.profileId,
        },
        // The board reports the failure on the clicked cell itself.
        suppressErrorPrompt: true,
      })
      .then((res) => ({
        siteId: input.siteId,
        foodId: input.foodId,
        foodName: input.foodName,
        profileId: res.data?.supply_profile_id ?? null,
      }))
      .catch((error) => rejectWithValue(getErrorMessage(error))),
);

/** Mirror a saved link move in the cached profiles, without a refetch. */
const applyIngredientLink = (
  profiles: SupplyProfileResponse[],
  link: {
    siteId: number;
    foodId: number;
    foodName: string;
    profileId: number | null;
  },
): SupplyProfileResponse[] =>
  profiles.map((profile) => {
    if (profile.jasper_site_id !== link.siteId || !profile.is_active) {
      return profile;
    }
    const without = (profile.ingredients ?? []).filter(
      (item) => item.food_id !== link.foodId,
    );
    if (profile.id !== link.profileId) {
      return without.length === (profile.ingredients ?? []).length
        ? profile
        : { ...profile, ingredients: without };
    }
    return {
      ...profile,
      ingredients: [
        ...without,
        {
          food_id: link.foodId,
          food_name: link.foodName,
          updated_at: new Date().toISOString(),
        },
      ],
    };
  });

/** Replace the matching profile in the list, or append it when new. */
const upsertProfile = (
  profiles: SupplyProfileResponse[],
  profile: SupplyProfileResponse | null,
): SupplyProfileResponse[] => {
  if (!profile) {
    return profiles;
  }
  const index = profiles.findIndex((item) => item.id === profile.id);
  if (index === -1) {
    return [...profiles, profile];
  }
  return profiles.map((item, i) => (i === index ? profile : item));
};

export const supplyProfileSlice = createSlice({
  name: "supplyProfiles",
  initialState,
  reducers: {
    clearError: (state) => ({ ...state, error: null }),
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSupplyProfiles.pending, (state) => ({
      ...state,
      isLoading: true,
      error: null,
    }));
    builder.addCase(
      fetchSupplyProfiles.fulfilled,
      (state, action: PayloadAction<SupplyProfileResponse[]>) => ({
        ...state,
        isLoading: false,
        profiles: action.payload,
      }),
    );
    builder.addCase(fetchSupplyProfiles.rejected, (state, action) => ({
      ...state,
      isLoading: false,
      error: action.payload as string,
    }));

    [
      createSupplyProfile,
      updateSupplyProfile,
      deactivateSupplyProfile,
      reactivateSupplyProfile,
    ].forEach((thunk) => {
      builder.addCase(thunk.pending, (state) => ({
        ...state,
        isSaving: true,
        error: null,
      }));
      builder.addCase(thunk.fulfilled, (state, action) => ({
        ...state,
        isSaving: false,
        profiles: upsertProfile(state.profiles, action.payload),
      }));
      builder.addCase(thunk.rejected, (state, action) => ({
        ...state,
        isSaving: false,
        error: action.payload as string,
      }));
    });

    // Link moves are per cell: they neither toggle isSaving nor set the
    // page-level error; the board surfaces the rejection itself.
    builder.addCase(setIngredientLink.fulfilled, (state, action) => ({
      ...state,
      profiles: applyIngredientLink(state.profiles, action.payload),
    }));
  },
});

export const supplyProfileActions = {
  ...supplyProfileSlice.actions,
  fetchSupplyProfiles,
  createSupplyProfile,
  updateSupplyProfile,
  deactivateSupplyProfile,
  reactivateSupplyProfile,
  setIngredientLink,
};

export const supplyProfileReducer = supplyProfileSlice.reducer;

export default supplyProfileReducer;
